import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { Permission } from 'app/core/models/permission';
import { EMPTY, noop, Observable } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { Actions, Select, Store, ofActionCompleted } from '@ngxs/store';
import { SettingsRolesPermissionState } from './state/state';
import { Settings } from './state/actions';
import { RolePermission } from './roles-permissions.types';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { AddRoleModalComponent } from './@components/add-role-modal/add-role-modal.component';
import { PermissionRole, Role } from 'app/core/models';
import { ConfirmService } from 'app/shared/components/widgets/dialog/confirm/confirm.service';
import { WarningComponent } from 'app/shared/components/widgets/dialog/warning/warning.component';
import { DialogModel } from 'app/shared/components/widgets/dialog/dialog.types';
import { HttpErrorResponse } from '@angular/common/http';

type SelectablePermission = Permission & { selected?: boolean };

@Component({
  selector: 'roles-permissions-settings',
  templateUrl: './roles-permissions.component.html',
  styleUrls: ['./roles-permissions.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RolesPermissionsComponent extends OnDestroyMixin implements OnInit {
  ui = { isLoading: false };
  selectedRole: RolePermission;
  roles$: Observable<RolePermission[]>;

  private lastRolesIds: number[] = undefined;

  @Select(SettingsRolesPermissionState.getRoles) stateRoles$: Observable<RolePermission[]>;
  @Select(SettingsRolesPermissionState.getPermissions) permissions$: Observable<SelectablePermission[]>;

  constructor(
    private readonly snackbar: MatSnackBar,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly store: Store,
    private readonly matDialog: MatDialog,
    private readonly actions$: Actions,
    private readonly confirmService: ConfirmService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.store.dispatch(new Settings.RolesPermissions.SetRoles());
    this.store.dispatch(new Settings.RolesPermissions.SetPermissions());

    this.roles$ = this.stateRoles$.pipe(
      tap((roles) => {
        if (roles?.length) {
          if (this.lastRolesIds !== undefined && roles.length > this.lastRolesIds.length) {
            const [firstId] = roles.map(({ id }) => id).filter((id) => !this.lastRolesIds.includes(id));
            this.selectedRole = roles.find(({ id }) => id === firstId);
          } else {
            if (this.selectedRole?.id && this.selectedRole.id !== roles[0].id) {
              if (!roles.find(({ id }) => id === this.selectedRole.id)) {
                this.selectedRole = roles[0];
              } else {
                this.selectedRole = roles.find(({ id }) => id === this.selectedRole.id);
              }
            } else {
              this.selectedRole = roles[0];
            }
          }

          this.lastRolesIds = roles.map(({ id }) => id);
        }
      }),
    );

    this.actions$
      .pipe(
        untilComponentDestroyed(this),
        ofActionCompleted(Settings.RolesPermissions.SetRoles),
        tap(() => {
          this.ui = { isLoading: false };
          this.changeDetectorRef.detectChanges();
        }),
      )
      .subscribe(noop);

    this.actions$
      .pipe(
        untilComponentDestroyed(this),
        ofActionCompleted(Settings.RolesPermissions.UpdatePermissions),
        tap(() => {
          this.snackbar.open('Permissions updated', null, { duration: 2000 });
        }),
      )
      .subscribe(noop);

    this.actions$
      .pipe(
        ofActionCompleted(Settings.RolesPermissions.CreateRole, Settings.RolesPermissions.EditRole),
        tap(({ action, result }) => {
          if (result.error) {
            const mode = action.model.id ? 'edit' : 'create';
            this.openRoleModal(mode, action.model);
            this.snackbar.open((result.error as HttpErrorResponse).error.error || `Could not ${mode} the role`, null, {
              duration: 2000,
            });
          }

          this.ui = { isLoading: false };
          this.changeDetectorRef.detectChanges();
        }),
      )
      .subscribe(noop);

    this.actions$
      .pipe(
        ofActionCompleted(Settings.RolesPermissions.DeleteRole),
        tap(({ action, result }) => {
          if (result.error) {
            const [errorMessage] = (result.error as HttpErrorResponse).error.role.errors;
            this.snackbar.open(errorMessage || 'Could not remove the role', null, {
              duration: 2000,
            });
          } else {
            this.snackbar.open('Role delete successfully', null, {
              duration: 2000,
            });
          }

          this.ui = { isLoading: false };
          this.changeDetectorRef.detectChanges();
        }),
      )
      .subscribe(noop);
  }

  roleSelected(role: RolePermission): void {
    this.selectedRole = role;
  }

  openRoleModal(mode: 'edit' | 'create' = 'create', seed: Partial<Role> = null): void {
    this.matDialog
      .open(AddRoleModalComponent, {
        panelClass: 'mailbox-compose-dialog',
        data: mode === 'edit' ? this.selectedRole : seed,
      })
      .afterClosed()
      .pipe(
        untilComponentDestroyed(this),
        switchMap((result: Partial<Role>) => {
          if (!result) {
            return EMPTY;
          }

          this.ui = { isLoading: true };
          this.changeDetectorRef.detectChanges();

          const action =
            mode === 'create'
              ? new Settings.RolesPermissions.CreateRole({ ...result })
              : new Settings.RolesPermissions.EditRole({ ...result });

          return this.store.dispatch(action);
        }),
        tap(() => {
          this.snackbar.open(mode === 'create' ? 'New role added.' : 'Role edited.', null, { duration: 2000 });
        }),
      )
      .subscribe(noop);
  }

  permissionsSaved(permissionsRoles: PermissionRole[]): void {
    this.ui = { isLoading: true };

    this.store.dispatch(
      new Settings.RolesPermissions.UpdatePermissions({
        roleId: this.selectedRole.id,
        permissionsRoles: permissionsRoles.map(({ permission, permission_value }) => ({
          permission_id: permission.id,
          permission_value,
        })),
      }),
    );
  }

  editRoleClicked(): void {
    this.openRoleModal('edit');
  }

  removeRoleClicked(): void {
    if (this.selectedRole.userCount > 0) {
      this.matDialog.open<WarningComponent, DialogModel>(WarningComponent, {
        data: {
          title: 'Delete role',
          message:
            'You cannot delete this role because there are currently users assigned to it.<br />Please reassign those users to another role in order to delete.',
        },
      });
    } else {
      this.confirmService
        .openConfirmationModal({
          title: 'Delete role',
        })
        .pipe(
          untilComponentDestroyed(this),
          tap((result) => {
            if (result) {
              this.store.dispatch(new Settings.RolesPermissions.DeleteRole(this.selectedRole.id));
            }
          }),
        )
        .subscribe(noop);
    }
  }
}
